/* eslint complexity: ["error", { max: 9 }] -- handlePlayingChange handles different play/pause/seeking scenarios */
/* eslint max-lines: ["error", {max: 470}] -- applies to entire file */
/* eslint max-lines-per-function: ["error", {max: 397}] -- applies to entire file */
/* eslint max-statements: ["error", {max: 22}] -- applies to entire file */
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import { differenceInSeconds, isAfter } from "date-fns";
import type { SourceLoadEventPayload } from "expo-video";
import isNil from "lodash/isNil";

import { AnalyticsFieldName } from "@/constants/analytics/Analytics";
import {
  WatchTrackingAnalyticsEvent,
  WatchTrackingAnalyticsFieldName,
} from "@/constants/analytics/WatchTracking";
import useUserInactivityTimeout from "@/hooks/useUserInactivityTimeout";
import type { MixpanelProps } from "@/services/Analytics";
import { useAnalytics } from "@/services/Analytics";
import { useAppUserSelector } from "@/store/reducers/appUser";
import type {
  PlaybackRateChangePayload,
  PlayingChangePayload,
  TimeUpdatePayload,
} from "@/ui/composed/media/videoPlayer/adapter/ExpoVideoPlayer";
import type {
  BaseVideoPlayerHandle,
  BaseVideoPlayerProps,
} from "@/ui/composed/media/videoPlayer/layers/BaseVideoPlayer";
import BaseVideoPlayer from "@/ui/composed/media/videoPlayer/layers/BaseVideoPlayer";
import { getUserRole } from "@/utils/analytics.util";
import { VideoActivityHeartbeatIntervalMs } from "@/utils/appInactivity.util";
import { toMs } from "@/utils/date.util";
import { isAndroid } from "@/utils/platform.util";

const Fields = WatchTrackingAnalyticsFieldName;
const Events = WatchTrackingAnalyticsEvent;

// iOS/Safari video players have skip buttons, which do not trigger events;
// Chrome/Firefox have arrow-key shortcuts for skipping which also do not trigger events,
// so we need to periodically check the current playback position to detect skips
const ProgressUpdateIntervalMillis = toMs({ seconds: 1 });

// threshold for detecting -5 and +5 second skips in Firefox (set to 4 seconds to allow for some margin of error in timing)
// iOS has -10 and +10 skip buttons, Safari has -15 and +15 skip buttons, so they are also captured by this threshold
const SkipThresholdSeconds = 4;

interface AnalyticsVideoPlayerProps
  extends
    ViewWithTestID,
    Omit<
      BaseVideoPlayerProps,
      "onPlaybackStatusUpdate" | "onUnloadRef" | "progressUpdateIntervalMillis"
    > {
  carouselIndex?: number;
  carouselTitle?: string;
  componentSource?: string;
  isAnalyticsEnabled?: boolean;
  title?: string;
}

const AnalyticsVideoPlayer = forwardRef<
  BaseVideoPlayerHandle,
  AnalyticsVideoPlayerProps
>(
  (
    // eslint-disable-next-line no-restricted-syntax -- this component relies on multiple props related to video tracking
    {
      carouselIndex,
      carouselTitle,
      componentSource,
      isAnalyticsEnabled = true,
      onError,
      onVideoSize,
      resizeMode,
      shouldAutoPlay,
      shouldShowControls,
      shouldShowThumbnail,
      size,
      styles,
      testID,
      thumbnailUrl,
      title,
      videoUrl,
    }: AnalyticsVideoPlayerProps,
    ref,
    // eslint-disable-next-line max-params
  ) => {
    const { externalId: userId, isSupport: isAppUserSupport } =
      useAppUserSelector();
    const { markActivity: markUserActivity } = useUserInactivityTimeout();
    const [videoLengthSeconds, setVideoLengthSeconds] = useState<
      number | undefined
    >();
    const [watchedIntervals, setWatchedIntervals] = useState<
      { end: number; playbackRate: number; start: number }[]
    >([]);
    const [latestPause, setLatestPause] = useState<
      { seconds: number; timestamp: Date } | undefined
    >();
    const [latestPlay, setLatestPlay] = useState<{
      playbackRate: number;
      seconds: number;
      timestamp: Date;
    }>({ playbackRate: 1, seconds: 0, timestamp: new Date() });

    const handleSourceLoad = ({ duration }: SourceLoadEventPayload) => {
      if (videoLengthSeconds) return;
      const roundedSeconds = Math.round(duration);
      setVideoLengthSeconds(roundedSeconds);
    };

    const { track: trackMixpanel } = useAnalytics();

    const track = useCallback(
      // eslint-disable-next-line max-params
      (event: string, props?: MixpanelProps) => {
        if (!isAnalyticsEnabled) return;
        trackMixpanel(event, {
          [AnalyticsFieldName.Component]: componentSource,
          [AnalyticsFieldName.UserRole]: getUserRole({
            isSupport: isAppUserSupport,
          }),
          [Fields.UserId]: userId,
          [Fields.Video.CarouselIndex]: carouselIndex,
          [Fields.Video.CarouselTitle]: carouselTitle,
          [Fields.Video.Length]: videoLengthSeconds,
          [Fields.Video.Title]: title,
          [Fields.Video.Url]: videoUrl,
          ...props,
        });
      },
      [
        carouselIndex,
        carouselTitle,
        componentSource,
        isAnalyticsEnabled,
        isAppUserSupport,
        title,
        trackMixpanel,
        userId,
        videoLengthSeconds,
        videoUrl,
      ],
    );

    const getElapsedSeconds = useCallback(() => {
      // elapsed time since last play (adjusted for playback speed)
      const elapsedPlaybackSeconds =
        differenceInSeconds(new Date(), latestPlay.timestamp) *
        latestPlay.playbackRate;

      // current video position = last play position + elapsed time since last play (adjusted for playback speed)
      return latestPlay.seconds + elapsedPlaybackSeconds;
    }, [latestPlay]);

    const toPercentage = useCallback(
      (seconds: number) =>
        videoLengthSeconds
          ? Math.round((seconds / videoLengthSeconds) * 1000) / 10
          : 0,
      [videoLengthSeconds],
    );

    const onUnload = useCallback(() => {
      const allIntervals = [...watchedIntervals];
      const isPlaying =
        isNil(latestPause) ||
        isAfter(latestPlay.timestamp, latestPause.timestamp);

      const currentSeconds = getElapsedSeconds();

      if (isPlaying) {
        const lastPlayIntervalBeforeUnload = {
          end: currentSeconds,
          playbackRate: latestPlay.playbackRate,
          start: latestPlay.seconds,
        };
        allIntervals.push(lastPlayIntervalBeforeUnload);
      }

      // sort allIntervals from earliest to latest start time
      // eslint-disable-next-line max-params
      allIntervals.sort((a, b) => a.start - b.start);
      const mergedIntervals =
        allIntervals.length > 0
          ? [
              {
                end: allIntervals[0].end,
                start: allIntervals[0].start,
              },
            ]
          : [];

      let actualWatchTime = 0;
      let actualDurationWatched = 0;
      for (const interval of allIntervals) {
        const intervalDuration = interval.end - interval.start;
        actualWatchTime += intervalDuration / interval.playbackRate;
        actualDurationWatched += intervalDuration;

        // the previous interval in the sorted array has an earlier start time
        const lastMergedInterval = mergedIntervals.at(-1);
        // if the current interval's end time falls within the previous interval, merge them
        if (lastMergedInterval && interval.start <= lastMergedInterval.end) {
          lastMergedInterval.end = Math.max(
            lastMergedInterval.end,
            interval.end,
          );
        } else {
          mergedIntervals.push({
            end: interval.end,
            start: interval.start,
          });
        }
      }

      const uniqueDurationWatched = mergedIntervals.reduce(
        // eslint-disable-next-line max-params
        (sum, interval) => sum + (interval.end - interval.start),
        0,
      );

      track(Events.Video.Closed, {
        [Fields.Cumulative.ActualDurationWatched]: actualDurationWatched,
        [Fields.Cumulative.ActualWatchTime]: actualWatchTime,
        [Fields.Cumulative.UniqueDurationWatched]: uniqueDurationWatched,
        [Fields.Cumulative.UniqueWatchedPercentage]: toPercentage(
          uniqueDurationWatched,
        ),
      });
    }, [
      watchedIntervals,
      latestPause,
      latestPlay,
      getElapsedSeconds,
      toPercentage,
      track,
    ]);

    const onUnloadRef = useRef(onUnload);
    useEffect(() => {
      onUnloadRef.current = onUnload;
    }, [onUnload]);

    const handleIsSeeking = ({
      seekFromSeconds,
      seekToSeconds,
    }: {
      seekFromSeconds: number;
      seekToSeconds: number;
    }) => {
      setLatestPlay({
        playbackRate: latestPlay.playbackRate,
        seconds: seekToSeconds,
        timestamp: new Date(),
      });

      const seekAmountSeconds = seekToSeconds - seekFromSeconds;
      if (seekToSeconds - seekFromSeconds === 0) return;

      track(Events.Video.Seek, {
        [Fields.Video.PositionPercentage]: toPercentage(seekToSeconds),
        [Fields.Video.PositionSeconds]: seekToSeconds,
        [Fields.Video.SeekAmountPercentage]: toPercentage(seekAmountSeconds),
        [Fields.Video.SeekAmountSeconds]: seekAmountSeconds,
        [Fields.Video.SeekFromSeconds]: seekFromSeconds,
        [Fields.Video.Speed]: latestPlay.playbackRate,
      });
    };

    const handleIsPlaying = ({
      currentSeconds,
      duration,
    }: {
      currentSeconds: number;
    } & Pick<PlayingChangePayload, "duration">) => {
      setLatestPlay({
        playbackRate: latestPlay.playbackRate,
        seconds: currentSeconds,
        timestamp: new Date(),
      });

      if (currentSeconds === 0) {
        // use duration from onPlayingChange since videoLengthSeconds may not be set yet (web does not fire onSourceLoad)
        return track(Events.Video.Started, {
          [Fields.Video.Length]: Math.round(duration),
        });
      }

      track(Events.Video.PlaybackResumed, {
        [Fields.Video.PositionPercentage]: toPercentage(currentSeconds),
        [Fields.Video.PositionSeconds]: currentSeconds,
        [Fields.Video.Speed]: latestPlay.playbackRate,
      });
    };

    const handleIsPausing = (currentSeconds: number) => {
      setLatestPause({
        seconds: currentSeconds,
        timestamp: new Date(),
      });

      // suppress pause event if user is seeking backwards/forwards from the actual elapsed time,
      // as seeking also triggers a rapid pause/play sequence
      const isSuppressedSeek =
        Math.abs(currentSeconds - getElapsedSeconds()) > 1;

      // Record interval unless this is a suppressed seek
      if (!isSuppressedSeek) {
        setWatchedIntervals([
          ...watchedIntervals,
          {
            end: currentSeconds,
            playbackRate: latestPlay.playbackRate,
            start: latestPlay.seconds,
          },
        ]);
      }

      if (currentSeconds === videoLengthSeconds) {
        return track(Events.Video.PlayedToEnd);
      }

      if (isSuppressedSeek) return;
      track(Events.Video.PlaybackPaused, {
        [Fields.Video.PositionPercentage]: toPercentage(currentSeconds),
        [Fields.Video.PositionSeconds]: currentSeconds,
        [Fields.Video.Speed]: latestPlay.playbackRate,
      });
    };

    const handlePlayingChange = ({
      currentTime,
      duration,
      isPlaying,
    }: PlayingChangePayload) => {
      // capture video length on initial play for web, as web does not fire onSourceLoad
      if (!videoLengthSeconds && duration) {
        setVideoLengthSeconds(Math.round(duration));
      }
      const secondsOnVideoPlayer = Math.round(currentTime);
      if (!isPlaying) return handleIsPausing(secondsOnVideoPlayer);

      if (!isNil(latestPause)) {
        // if user pauses video, seeks to a new position, and resumes at new position
        const isSeekFromPause = latestPause.seconds !== secondsOnVideoPlayer;

        if (isSeekFromPause) {
          // Interval already recorded by pause handler - just update play position
          return handleIsSeeking({
            seekFromSeconds: latestPause.seconds,
            seekToSeconds: secondsOnVideoPlayer,
          });
        }

        // seeking while the video is playing triggers a rapid pause/play sequence at a position different from the last watched interval
        const lastIntervalEndSeconds = watchedIntervals.at(-1)?.end;
        const isSeekFromPlay =
          isNil(lastIntervalEndSeconds) ||
          Math.abs(secondsOnVideoPlayer - lastIntervalEndSeconds) > 1;

        if (isSeekFromPlay) {
          const elapsedSeconds = getElapsedSeconds();
          setWatchedIntervals([
            ...watchedIntervals,
            {
              end: elapsedSeconds,
              playbackRate: latestPlay.playbackRate,
              start: latestPlay.seconds,
            },
          ]);
          return handleIsSeeking({
            seekFromSeconds: elapsedSeconds,
            seekToSeconds: secondsOnVideoPlayer,
          });
        }
      }

      // pass duration, since it is not available in onSourceLoad for web
      handleIsPlaying({ currentSeconds: secondsOnVideoPlayer, duration });
    };

    const handlePlaybackRateChange = ({
      currentTime,
      playbackRate,
    }: PlaybackRateChangePayload) => {
      track(Events.Video.PlaybackRateChanged, {
        [Fields.Video.Speed]: playbackRate,
        [Fields.Video.SpeedPrevious]: latestPlay.playbackRate,
      });
      const secondsOnVideoPlayer = Math.round(currentTime);
      setWatchedIntervals([
        ...watchedIntervals,
        {
          end: secondsOnVideoPlayer,
          playbackRate: latestPlay.playbackRate,
          start: latestPlay.seconds,
        },
      ]);
      setLatestPlay({
        playbackRate,
        seconds: secondsOnVideoPlayer,
        timestamp: new Date(),
      });
    };

    // handlePlayingChange already handles skip button presses on Android
    const handleTimeUpdate = isAndroid()
      ? undefined
      : ({ currentTime, isPlaying }: TimeUpdatePayload) => {
          // only check for button-skips when the video is playing,
          // as seeking after a pause is already captured by handlePlayingChange
          if (!isPlaying) return;

          const elapsedSeconds = getElapsedSeconds();
          const hasPressedSkipButton =
            Math.abs(currentTime - elapsedSeconds) > SkipThresholdSeconds;
          if (!hasPressedSkipButton) return;

          const seekToSeconds = Math.round(currentTime);
          // manually call handleIsPausing to capture the previous interval
          handleIsPausing(seekToSeconds);
          handleIsSeeking({
            seekFromSeconds: elapsedSeconds,
            seekToSeconds,
          });
        };

    useEffect(() => {
      if (!videoLengthSeconds) return;

      const interval = setInterval(() => {
        markUserActivity();
      }, VideoActivityHeartbeatIntervalMs);

      return () => clearInterval(interval);
    }, [videoLengthSeconds, markUserActivity]);

    return (
      <BaseVideoPlayer
        onError={onError}
        onPlaybackRateChange={handlePlaybackRateChange}
        onPlayingChange={handlePlayingChange}
        onSourceLoad={handleSourceLoad}
        onTimeUpdate={handleTimeUpdate}
        onUnloadRef={onUnloadRef}
        onVideoSize={onVideoSize}
        progressUpdateIntervalMillis={ProgressUpdateIntervalMillis}
        ref={ref}
        resizeMode={resizeMode}
        shouldAutoPlay={shouldAutoPlay}
        shouldShowControls={shouldShowControls}
        shouldShowThumbnail={shouldShowThumbnail}
        size={size}
        styles={styles}
        testID={testID}
        thumbnailUrl={thumbnailUrl}
        videoUrl={videoUrl}
      />
    );
  },
);
AnalyticsVideoPlayer.displayName = "AnalyticsVideoPlayer";

export type { AnalyticsVideoPlayerProps };
export default AnalyticsVideoPlayer;
